import { SearchTextInput } from '@frontend/components/basic-forms';
import { CommonMessage } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { SpotExtendedClient } from '@frontend/spot/api';
import { SpotExtended } from '@frontend/spot/types';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

export const SpotOverview = () => {
    const { currentAccount } = useAccountRepository();
    const [spots, setSpots] = useState<SpotExtended[]>([]);
    const navigate = useNavigate();
    const [query, setQuery] = useState<{ index: number; filterValue: string }>({ index: 0, filterValue: '' });
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const handleFilterChange = (value: string) => {
        setQuery({ index: 0, filterValue: value });
    };

    const handleLoadMore = () => {
        if (!isFetching) {
            setQuery((prev) => ({ ...prev, index: prev.index + 1 }));
        }
    };

    useEffect(() => {
        setIsFetching(true);
        SpotExtendedClient.fetchSpotExtendeds({
            account_id: currentAccount,
            size: '8',
            index: query.index.toString(),
            ...(query.filterValue && { name: query.filterValue })
        })
            .then((res) => {
                setTotalCount(res.count);
                if (query.index === 0) {
                    setSpots(res.results);
                } else {
                    setSpots((prev) => {
                        const existingIds = new Set(prev.map((spot) => spot.id));
                        const newSpots = res.results.filter((spot) => !existingIds.has(spot.id));
                        return [...prev, ...newSpots];
                    });
                }
            })
            .finally(() => {
                setIsFetching(false);
            });
    }, [currentAccount, query]);

    return (
        <div className='flex w-full flex-col items-center justify-center gap-14 self-stretch p-14'>
            <div className='flex flex-col items-center justify-start gap-4'>
                <div className='text-foreground-default justify-start text-3xl font-semibold'>
                    <FormattedMessage
                        id='spots.overview.title'
                        defaultMessage='Find your distriSPOT'
                    />
                </div>
                <SearchTextInput
                    value={query.filterValue}
                    onChange={handleFilterChange}
                />
            </div>
            <div className='flex flex-col items-center justify-start gap-6 self-stretch'>
                <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                    <div className='grid grid-cols-4 items-start justify-start gap-3 self-stretch'>
                        {spots.map((spot) => (
                            <div
                                key={spot.id}
                                onClick={() => navigate(`./${spot.id}`)}
                                className='bg-background-surface hover:bg-background-surface/70 outline-border-default inline-flex flex-1 cursor-pointer flex-col items-start justify-between gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                                <div className='text-foreground-default w-fit justify-start text-xl font-semibold'>{spot.name}</div>
                                <div className='flex flex-col items-start justify-start gap-4 self-stretch'>
                                    <div className='text-foreground-default flex flex-wrap justify-start gap-1 self-stretch text-base font-medium break-words'>
                                        {spot.street && <span>{spot.street} </span>}
                                        {spot.number && <span>{spot.number}, </span>}
                                        {spot.zip_code && <span>{spot.zip_code} </span>}
                                        {spot.city && <span>{spot.city}</span>}
                                    </div>
                                    <div
                                        data-variant='Default'
                                        className='bg-border-default inline-flex h-6 min-w-6 items-center justify-center gap-2.5 rounded px-1'>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>
                                            <div className='flex gap-1 lowercase'>
                                                <span>{spot.num_modules}</span>
                                                <span>{CommonMessage.OBJECTS.MODULE.PLURAL}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {totalCount > spots.length && (
                    <div
                        onClick={handleLoadMore}
                        className={`justify-start text-center text-base font-medium ${
                            isFetching ? 'text-foreground-muted cursor-not-allowed' : 'text-brand-primary cursor-pointer'
                        }`}>
                        <FormattedMessage
                            id='spots.overview.load-more'
                            defaultMessage='Load more distriSPOTs'
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
